// @flow

import type { Page, PageItem, PageNode, PageNodeProp } from "@/utils/JsonPageRenderer/types";

type NodeProps = {
    [string]: any,
};

type Node = {
    /**
     * A unique id of this node, generated by combining the original nodes _id and _parentId
     */
    id: string,

    /**
     * The type of the original node.
     */
    type: string,

    /**
     * The children of this node (All nodes linked to this node by _parentId).
     */
    children: Node[],

    /**
     * The un-flattened properties of this node.
     */
    props: NodeProps,
};

/**
 * Gets the value of a prop.
 *
 * @param prop
 * @return {null|string|boolean|number}
 */
function getPropValue(prop: PageNodeProp): string | number | boolean | null {
    if (typeof prop.string === "string") {
        return prop.string;
    }

    if (typeof prop.number === "number") {
        return prop.number;
    }

    if (typeof prop.boolean === "boolean") {
        return prop.boolean;
    }

    return null;
}

/**
 * Un-flatten a prop into an object.
 * Example: { prop: "md.span", number: 5 } -> { md: { span: 5 } }
 * @param prop
 * @return {string|{}}
 */
function unflatten(prop: PageNodeProp): NodeProps {
    return prop.prop
        .split(".")
        .reverse()
        .reduce((acc, current, index) => {
            if (index === 0) {
                return { [current]: getPropValue(prop) };
            }
            return { [current]: acc };
        }, {});
}

/**
 * Deep merge two objects.
 * @param a
 * @param b
 * @return {*}
 */
const mergeProps: any = (a: any, b: any) => {
    for (const key of Object.keys(b)) {
        if (!(key in a) || typeof b[key] !== "object") {
            a[key] = b[key];
        } else {
            mergeProps(a[key], b[key]);
        }
    }
    return a;
}

/**
 * Utility class for rebuilding a flattened page into a tree-like structure.
 */
class TreeBuilder {
    page: Page;

    constructor(page: Page) {
        this.page = page;
    }

    /**
     * Rebuild the flattened nodes into a tree
     * @return {Array<Node>}
     */
    buildTree(): Node[] {
        return this.page.nodes
            .filter(node => node._parentId == null)
            .map(node => this.transform(node));
    }

    build(): PageItem[] {
        return TreeBuilder.toPageItems(this.buildTree());
    }

    /**
     * Converts each node into page item.
     *
     * @param nodes
     * @return {Array<{children: PageItem[], id: *, type: *, props: *}>}
     */
    static toPageItems(nodes: Node[]): PageItem[] {
        return nodes.map(node => ({
            id: node.id,
            children: this.toPageItems(node.children),
            type: node.type,
            props: node.props,
        }));
    }

    /**
     * Merge a list of un-flattened props.
     * @param props
     * @return {T|*}
     */
    merge(props: NodeProps[]): NodeProps {
        return props.reduce((acc, current) => mergeProps(acc, current), {});
    }

    /**
     * Un-flattens the props array and merges them into a single object.
     *
     * @param node
     * @return {NodeProps}
     */
    createProps(node: PageNode): NodeProps {
        const props = node.props.map(prop => unflatten(prop));
        return this.merge(props);
    }

    /**
     * Transforms a flattened node to a node with children and props.
     *
     * @param node
     * @return {{children: Array<Node>, id: (string|*), type: string, props: NodeProps}}
     */
    transform(node: PageNode): Node {
        // Create a unique id for this node by combine the _id and _parentId
        const id =
            typeof node._parentId === "number"
                ? `${node._id}-${node._parentId}`
                : String(node._id);

        // Grab all child nodes that are linked to this node and transform them as well.
        const children = this.takeChildNodes(node).map(childNode =>
            this.transform(childNode),
        );

        // Un-flatten the props.
        const props = this.createProps(node);

        return {
            id: id,
            type: node.type,
            children: children,
            props: props,
        };
    }

    /**
     * Finds all the children linked to the given node.
     *
     * @param parent
     * @return {Array<$NonMaybeType<T>>|Array<T>}
     */
    takeChildNodes(parent: PageNode): PageNode[] {
        return this.page.nodes.filter(node => node._parentId === parent._id);
    }
}

export default TreeBuilder;
